import { App } from '@capacitor/app';
import { KeepAwake } from '@capacitor-community/keep-awake';
import type { PluginListenerHandle } from '@capacitor/core';
import { runtimeConfig } from './runtime';

type WakeLockSentinelLike = {
  readonly released?: boolean;
  addEventListener?: (type: 'release', listener: () => void) => void;
  release: () => Promise<void>;
};

type WakeLockNavigator = Navigator & {
  wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinelLike>;
  };
};

export type MeditationAppStateListener = (isActive: boolean, changedAt: number) => void;

let webWakeLock: WakeLockSentinelLike | null = null;

const requestWebWakeLock = async (): Promise<boolean> => {
  const wakeLock = (typeof navigator === 'undefined'
    ? undefined
    : (navigator as WakeLockNavigator).wakeLock);

  if (!wakeLock || (typeof document !== 'undefined' && document.visibilityState !== 'visible')) {
    return false;
  }

  if (webWakeLock && !webWakeLock.released) {
    return true;
  }

  try {
    const requestedLock = await wakeLock.request('screen');
    webWakeLock = requestedLock;
    requestedLock.addEventListener?.('release', () => {
      if (webWakeLock === requestedLock) {
        webWakeLock = null;
      }
    });

    return true;
  } catch {
    webWakeLock = null;
    return false;
  }
};

/**
 * Keeps the display awake while a guided meditation is actively running.
 * Native plugin details and the browser Screen Wake Lock API stay behind this seam.
 */
export const requestMeditationWakeLock = async (): Promise<boolean> => {
  if (!runtimeConfig.isNativePlatform) {
    return requestWebWakeLock();
  }

  try {
    const support = await KeepAwake.isSupported();

    if (!support.isSupported) {
      return false;
    }

    await KeepAwake.keepAwake();
    return true;
  } catch {
    return false;
  }
};

/** Best-effort cleanup: failure to restore sleep must not break the player. */
export const releaseMeditationWakeLock = async (): Promise<void> => {
  if (runtimeConfig.isNativePlatform) {
    try {
      await KeepAwake.allowSleep();
    } catch {
      // The plugin may be absent or the native activity may already be gone.
    }

    return;
  }

  const lockToRelease = webWakeLock;
  webWakeLock = null;

  try {
    await lockToRelease?.release();
  } catch {
    // Browsers can release the sentinel automatically when the page is hidden.
  }
};

/**
 * Normalizes Capacitor's native lifecycle and document visibility changes.
 * The timestamp is captured at the event boundary so reconciliation never relies
 * on a timer callback that the operating system may suspend.
 */
export const subscribeToMeditationAppState = (
  listener: MeditationAppStateListener,
): (() => void) => {
  let disposed = false;
  let handle: PluginListenerHandle | null = null;

  void App.addListener('appStateChange', ({ isActive }) => {
    if (!disposed) {
      listener(isActive, Date.now());
    }
  })
    .then((registeredHandle) => {
      if (disposed) {
        void registeredHandle.remove();
        return;
      }

      handle = registeredHandle;
    })
    .catch(() => {
      // Lifecycle observation is progressive enhancement. Timestamp-based ticks
      // still keep foreground sessions correct if listener registration fails.
    });

  return () => {
    disposed = true;
    void handle?.remove();
  };
};

